import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import * as similarityService from '@/api/services/similarity.service';
import type {
  SimilarityGroupFilters,
  SimilarityGroupUpdatePayload,
  SimilarityRunPayload,
} from '@/api/types/similarity.types';

import { PHOTO_KEYS } from './usePhotos';

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const SIMILARITY_KEYS = {
  all: ['similarity'] as const,
  lists: () => [...SIMILARITY_KEYS.all, 'list'] as const,
  list: (filters?: SimilarityGroupFilters) =>
    [...SIMILARITY_KEYS.lists(), filters] as const,
  details: () => [...SIMILARITY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...SIMILARITY_KEYS.details(), id] as const,
};

// ---------------------------------------------------------------------------
// useSimilarityGroups - list groups for an event
// ---------------------------------------------------------------------------

export function useSimilarityGroups(filters?: SimilarityGroupFilters) {
  return useQuery({
    queryKey: SIMILARITY_KEYS.list(filters),
    queryFn: () => similarityService.getSimilarityGroups(filters),
    enabled: !!filters?.event,
  });
}

// ---------------------------------------------------------------------------
// useSimilarityGroup - single group detail
// ---------------------------------------------------------------------------

export function useSimilarityGroup(groupId: string) {
  return useQuery({
    queryKey: SIMILARITY_KEYS.detail(groupId),
    queryFn: () => similarityService.getSimilarityGroup(groupId),
    enabled: !!groupId,
  });
}

// ---------------------------------------------------------------------------
// useUpdateSimilarityBestPhoto - override best photo
// ---------------------------------------------------------------------------

export function useUpdateSimilarityBestPhoto() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      groupId,
      payload,
    }: {
      groupId: string;
      payload: SimilarityGroupUpdatePayload;
    }) => similarityService.updateSimilarityGroupBestPhoto(groupId, payload),
    onSuccess: (_data, variables) => {
      // Invalidate both the group detail and the photos list
      queryClient.invalidateQueries({
        queryKey: SIMILARITY_KEYS.detail(variables.groupId),
      });
      queryClient.invalidateQueries({
        queryKey: SIMILARITY_KEYS.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: PHOTO_KEYS.lists(),
      });
      toast.success(t('similarity.bestPhotoUpdated'));
    },
    onError: () => {
      toast.error(t('similarity.bestPhotoUpdateError'));
    },
  });
}

// ---------------------------------------------------------------------------
// useRunSimilarity - trigger computation
// ---------------------------------------------------------------------------

export function useRunSimilarity() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: SimilarityRunPayload) =>
      similarityService.runSimilarity(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: SIMILARITY_KEYS.list({ event: variables.event }),
      });
      toast.success(t('similarity.computationQueued'));
    },
    onError: () => {
      toast.error(t('similarity.computationError'));
    },
  });
}
